import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import supabase, { setSupabaseWorkspace } from "@shared/api/supabase";
import type { Tables } from "@shared/database.types";
import { useAuth } from "@/context/auth";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { blastApi } from "@flows/api/blast-api";

type Workspace = Tables<"workspaces">;

type MembershipWithWorkspace = {
  role: string;
  workspace: Workspace;
};

type Ctx = {
  initialized: boolean;
  loading: boolean;
  error?: string;
  memberships: MembershipWithWorkspace[];
  active: Workspace | null;
  /** increments each time workspace changes; useful for keying components */
  wsChangeCount: number;
  refresh: () => Promise<void>;
  selectWorkspace: (workspaceId: string) => void;
  createWorkspace: (input: { name: string; slug?: string | null }) => Promise<Workspace>;
  /** switch active workspace and orchestrate global reload */
  switchWorkspace: (workspaceId: string) => Promise<void>;
};

const WorkspaceContext = createContext<Ctx | null>(null);
const WS_STORAGE_KEY = "bunker.ws";

export function WorkspaceProvider({ children }: { children: React.ReactNode }) {
  const { user, initialized: authReady } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [initialized, setInitialized] = useState(false);
  const [loading, setLoading] = useState(false);
  const [memberships, setMemberships] = useState<MembershipWithWorkspace[]>([]);
  const [active, setActive] = useState<Workspace | null>(null);
  const [error, setError] = useState<string | undefined>(undefined);
  const [wsChangeCount, setWsChangeCount] = useState(0);

  const refresh = useCallback(async () => {
    if (!user?.id) {
      setMemberships([]);
      setActive(null);
      setInitialized(true);
      return;
    }
    setLoading(true);
    setError(undefined);
    try {
      // Guard against hanging network calls: apply a short timeout.
      const { data: mems, error: mErr } = (await Promise.race([
        supabase
          .from("memberships")
          .select("workspace_id, role, created_at")
          .eq("user_id", user.id),
        new Promise((_, reject) => setTimeout(() => reject(new Error("Timed out loading memberships")), 4000)),
      ])) as any;
      if (mErr) throw mErr;

      if (!mems || mems.length === 0) {
        setMemberships([]);
        setActive(null);
        setInitialized(true);
        return;
      }

      const wsIds = mems.map((m) => m.workspace_id);
      const { data: wss, error: wErr } = (await Promise.race([
        supabase
          .from("workspaces")
          .select("*")
          .in("id", wsIds as string[]),
        new Promise((_, reject) => setTimeout(() => reject(new Error("Timed out loading workspaces")), 4000)),
      ])) as any;
      if (wErr) throw wErr;

      const byId = new Map((wss ?? []).map((w) => [w.id, w] as const));
      const enriched: MembershipWithWorkspace[] = (mems ?? [])
        .map((m) => {
          const w = byId.get(m.workspace_id);
          return w ? { role: m.role, workspace: w } : null;
        })
        .filter(Boolean) as MembershipWithWorkspace[];

      setMemberships(enriched);

      const stored = localStorage.getItem(WS_STORAGE_KEY);
      const picked =
        enriched.find((x) => x.workspace.id === stored)?.workspace ??
        enriched[0]?.workspace ??
        null;

      setActive(picked);
      if (picked) localStorage.setItem(WS_STORAGE_KEY, picked.id);
      setInitialized(true);
    } catch (e: any) {
      console.error("Workspace refresh failed:", e);
      setError(e?.message ?? String(e));
      setMemberships([]);
      setActive(null);
      setInitialized(true);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (authReady) void refresh();
  }, [authReady, refresh]);

  // Keep API client in sync with the active workspace (headers & SSE params)
  useEffect(() => {
    blastApi.setWorkspace(active?.id ?? null);
    setSupabaseWorkspace(active?.id ?? null);
  }, [active?.id]);

  const selectWorkspace = useCallback(
    (workspaceId: string) => {
      const match = memberships.find((m) => m.workspace.id === workspaceId)?.workspace ?? null;
      setActive(match);
      if (match) localStorage.setItem(WS_STORAGE_KEY, match.id);
      else localStorage.removeItem(WS_STORAGE_KEY);
    },
    [memberships]
  );

  const switchWorkspace = useCallback(
    async (workspaceId: string) => {
      // 1) swap active reference from current memberships list
      selectWorkspace(workspaceId);
      // 2) cancel in-flight queries, then nuke cache so all data refetch under the new workspaceId
      await qc.cancelQueries();
      qc.clear();
      // 3) bump change counter for consumers to key/reset on
      setWsChangeCount((n) => n + 1);
      // 4) navigate to flows root (avoids stale detail routes)
      navigate("/flows", { replace: true });
      // 5) refresh memberships/active from server to ensure consistency
      await refresh();
    },
    [navigate, qc, refresh, selectWorkspace]
  );

  const createWorkspace = useCallback(
    async ({ name, slug }: { name: string; slug?: string | null }) => {
      if (!user?.id) throw new Error("Not authenticated");
      const { data: ws, error: err1 } = await supabase
        .from("workspaces")
        .insert({ name, slug: slug ?? null, created_by: user.id })
        .select()
        .single();
      if (err1) throw err1;

      // Membership is created by the backend automatically on workspace creation
      await refresh();
      setActive(ws);
      localStorage.setItem(WS_STORAGE_KEY, ws.id);
      return ws;
    },
    [refresh, user?.id]
  );

  const value = useMemo<Ctx>(
    () => ({ initialized, loading, error, memberships, active, wsChangeCount, refresh, selectWorkspace, createWorkspace, switchWorkspace }),
    [initialized, loading, error, memberships, active, wsChangeCount, refresh, selectWorkspace, createWorkspace, switchWorkspace]
  );

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

export function useWorkspace(): Ctx {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) throw new Error("useWorkspace must be used within WorkspaceProvider");
  return ctx;
}
